import styles from './index.module.css'

import React from 'react'

import { FilterComponentProps, FilterOperatorOption } from 'types/filter-page'

import { Flex, Text, TextField } from 'components/ds'

import { useKeyPress } from 'hooks/use-key-press'

import { FilterOperatorSelector } from '../../filter-operator-selector'
import { getBetweenRangeErrors } from '../../utils'
import { FilterErrorText } from '../components'
import { useNumericValidation } from './use-numeric-validation'

type NumberFormProps = FilterComponentProps & {
  yearOperators?: boolean
}

const NumberForm = ({
  label,
  value,
  onChange,
  operator = 'equals',
  yearOperators = false,
  onPress,
  onValidityChange,
}: NumberFormProps) => {
  const { handleKeyDown } = useKeyPress('Enter', onPress)

  const { letterErrors, hasActiveLetterError, handleDigitChange } =
    useNumericValidation({ operator, onValidityChange })

  const { startMissing, endMissing, isIncomplete } =
    getBetweenRangeErrors(value)

  // Text input with numeric inputMode so we can intercept and reject letters.
  const createNumberInput = (
    inputValue: string,
    onChangeHandler: (_value: string) => void,
    shouldAutoFocus = true,
    hasError = false,
    doubleInput = false,
  ) => (
    <TextField.Root
      type="text"
      inputMode="numeric"
      value={inputValue}
      onChange={e => onChangeHandler(e.target.value)}
      onKeyDown={handleKeyDown}
      placeholder={label}
      autoFocus={shouldAutoFocus}
      color={hasError ? 'red' : undefined}
      className={[
        doubleInput ? styles.betweenInput : styles.single,
        hasError ? styles.error : '',
      ]
        .filter(Boolean)
        .join(' ')}
    />
  )

  const singleInput = (
    <Flex direction="column" gap="1">
      {createNumberInput(
        value || '',
        raw =>
          handleDigitChange('single', raw, digits =>
            onChange(digits, operator),
          ),
        true,
        letterErrors.single,
      )}
      {letterErrors.single && (
        <FilterErrorText>Only numbers allowed</FilterErrorText>
      )}
    </Flex>
  )

  const allFilterOperators: FilterOperatorOption[] = [
    {
      value: 'equals',
      component: singleInput,
    },
    {
      value: 'between',
      component: (
        <Flex direction="column" gap="1">
          <Flex gap="2" align="center" className={styles.betweenContainer}>
            {createNumberInput(
              value?.[0] || '',
              raw =>
                handleDigitChange('start', raw, digits =>
                  onChange([digits, value?.[1] ?? ''], operator),
                ),
              true,
              letterErrors.start || startMissing,
              true,
            )}
            <Text size="2">-</Text>
            {createNumberInput(
              value?.[1] || '',
              raw =>
                handleDigitChange('end', raw, digits =>
                  onChange([value?.[0] ?? '', digits], operator),
                ),
              false,
              letterErrors.end || endMissing,
              true,
            )}
          </Flex>
          {hasActiveLetterError ? (
            <FilterErrorText>Only numbers allowed</FilterErrorText>
          ) : (
            isIncomplete && <FilterErrorText>Enter both values</FilterErrorText>
          )}
        </Flex>
      ),
    },
    {
      value: yearOperators ? 'lte' : 'lt',
      component: singleInput,
    },
    {
      value: yearOperators ? 'gte' : 'gt',
      component: singleInput,
    },
  ]

  return (
    <FilterOperatorSelector
      operator={operator}
      onChange={onChange}
      operators={allFilterOperators}
    />
  )
}

export default NumberForm
